function capitalizeRound(key) {
  return key
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function restructureToKnockoutArray(data, category, isSOC = false) {
  if (!data?.[category]) return null;

  const knockout = data[category];
  const rounds = [];

  // Check if this is SOC format by looking for tournament_structure
  const hasSOCStructure = data.tournament_structure?.type === "SOC";
  const useSOCMapping = isSOC || hasSOCStructure;

  function mapMatch(match, roundName, matchNumber) {
    function buildClub(school, score, penalty_score = 0) {
      return {
        name: school?.school_name ?? null,
        score: score ?? 0,
        penalty_score: penalty_score ?? 0,
        id: school?.id ?? null,
      };
    }

    const home = buildClub(match.school_1, match.school_1_score, match.school_1_penalty_score);
    const away = buildClub(match.school_2, match.school_2_score, match.school_2_penalty_score);

    if (home.name && away.name) {
      if (home.score > away.score) {
        home.winner = true;
        // Check if match_note exists, status is Full Time, and set winner_penalty flag if applicable
        if (match.match_note?.length > 0 && match.match_status === "Full Time") {
          home.winner_penalty = true;
        }
      } else if (away.score > home.score) {
        away.winner = true;
        // Check if match_note exists, status is Full Time, and set winner_penalty flag if applicable
        if (match.match_note?.length > 0 && match.match_status === "Full Time") {
          away.winner_penalty = true;
        }
      } else if (home.penalty_score > away.penalty_score) {
        // Handle tie with penalties
        home.winner = true;
        // Check if match_note exists, status is Full Time, and set winner_penalty flag if applicable
        if (match.match_note?.length > 0 && match.match_status === "Full Time") {
          home.winner_penalty = true;
        }
      } else if (away.penalty_score > home.penalty_score) {
        // Handle tie with penalties
        away.winner = true;
        // Check if match_note exists, status is Full Time, and set winner_penalty flag if applicable
        if (match.match_note?.length > 0 && match.match_status === "Full Time") {
          away.winner_penalty = true;
        }
      }
    }

    return {
      knockout_round: match?.knockout_round,
      round_name: roundName,
      match_number: matchNumber,
      home_club: home,
      away_club: away,
    };
  }

  // Define round order based on tournament type
  let roundOrder;
  let roundNameMapping = {};

  if (useSOCMapping) {
    // SOC Tournament Structure:
    // top_16 = Round of 16 (Quarterfinals in display)
    // quarterfinal = Semifinals (in API but actually semifinals)
    // semifinal = Finals (in API but actually finals)
    // final = Champion
    roundOrder = ["top_16", "quarterfinal", "semifinal", "final", "third_place"];
    roundNameMapping = {
      top_16: "Round Of 16",
      quarterfinal: "Quarterfinal",
      semifinal: "Semifinal",
      final: "Final",
      champion: "Champion",
      third_place: "Third Place",
    };
  } else {
    // Regular tournament structure
    roundOrder = ["top_16", "quarterfinal", "semifinal", "third_place", "final"];
    roundNameMapping = {
      top_16: "Round Of 16",
      quarterfinal: "Quarterfinal",
      semifinal: "Semifinal",
      third_place: "Third Place",
      final: "Final",
    };
  }

  for (const roundKey of roundOrder) {
    const matches = knockout[roundKey];
    if (!Array.isArray(matches)) continue;

    const displayRoundName = roundNameMapping[roundKey] || capitalizeRound(roundKey);
    const roundMatches = matches.map((match, index) =>
      mapMatch(match, displayRoundName, index + 1),
    );

    rounds.push(roundMatches);
  }

  // Handle champion block differently for SOC vs regular tournaments
  let finalMatch, thirdMatch, semifinalMatches;

  if (useSOCMapping) {
    // In SOC:
    // - knockout.semifinal is actually the final match
    // - knockout.final is the champion declaration
    // - knockout.quarterfinal is actually the semifinal matches
    finalMatch = knockout.final?.[0] || {}; // This is the actual final
    thirdMatch = knockout.third_place?.[0] || {}; // third_place match typically
    semifinalMatches = knockout.quarterfinal || []; // These are actual semifinals
  } else {
    // Regular tournament structure
    finalMatch = knockout.final?.[0] || {};
    thirdMatch = knockout.third_place?.[0] || {};
    semifinalMatches = knockout.semifinal || [];
  }

  function getClubInfo(school, position, isWinner = false) {
    return {
      name: school?.school_name ?? null,
      id: school?.id ?? null,
      position,
      isWinner,
    };
  }

  // Check if the final match has finished (both teams have scores)
  const isFinalFinished =
    finalMatch.school_1_score !== undefined &&
    finalMatch.school_2_score !== undefined &&
    finalMatch.school_1 &&
    finalMatch.school_2 &&
    finalMatch?.match_status === "Full Time";

  // Get scores for determining winner
  const school1Score = Number(finalMatch.school_1_score) || 0;
  const school2Score = Number(finalMatch.school_2_score) || 0;

  // Determine winner and runner-up
  let club_1st, club_2nd;

  if (isFinalFinished) {
    // Match is finished, determine winner based on scores
    if (school1Score > school2Score) {
      club_1st = getClubInfo(finalMatch.school_1, "Champion", true);
      club_2nd = getClubInfo(finalMatch.school_2, "Runner-up", false);
    } else if (school2Score > school1Score) {
      club_1st = getClubInfo(finalMatch.school_2, "Champion", true);
      club_2nd = getClubInfo(finalMatch.school_1, "Runner-up", false);
    } else {
      // Handle tie - check for penalty shootout
      const school1PenaltyScore = Number(finalMatch.school_1_penalty_score) || 0;
      const school2PenaltyScore = Number(finalMatch.school_2_penalty_score) || 0;

      if (school1PenaltyScore > school2PenaltyScore) {
        club_1st = getClubInfo(finalMatch.school_1, "Champion", true);
        club_2nd = getClubInfo(finalMatch.school_2, "Runner-up", false);
      } else if (school2PenaltyScore > school1PenaltyScore) {
        club_1st = getClubInfo(finalMatch.school_2, "Champion", true);
        club_2nd = getClubInfo(finalMatch.school_1, "Runner-up", false);
      } else {
        // Still tied even after penalties or no penalty data
        club_1st = getClubInfo(finalMatch.school_1, "TBD", true);
        club_2nd = getClubInfo(finalMatch.school_2, "TBD", true);
      }
    }

    club_1st.champion_name = club_1st.name;
    club_2nd.runner_up_name = club_2nd.name;
  } else {
    club_1st = getClubInfo(null, "Champion", false);
    club_2nd = getClubInfo(null, "Runner-up", false);
    club_1st.champion_name = "TBD";
    club_2nd.runner_up_name = "TBD";
  }

  let club_3rd, club_4th;

  // Logic for third place match. A dedicated perebutan-juara-3 only counts once
  // it's actually played (Full Time) — sebelumnya non-SOC ga cek status, jadi
  // match yang belum main (score 0-0) dianggap seri & langsung ngasih juara 3.
  const isThirdPlaceFinished = isSOC
    ? thirdMatch.school_1_score !== undefined &&
      thirdMatch.school_2_score !== undefined &&
      thirdMatch.school_1 &&
      thirdMatch.school_2 &&
      thirdMatch?.match_status === "Full Time"
    : thirdMatch.school_1_score !== undefined &&
      thirdMatch.school_2_score !== undefined &&
      thirdMatch.school_1 &&
      thirdMatch.school_2 &&
      thirdMatch?.match_status === "Full Time";
  if (isSOC) {
    if (isThirdPlaceFinished) {
      // Third place match exists and has finished
      const thirdSchool1Score = Number(thirdMatch.school_1_score) || 0;
      const thirdSchool2Score = Number(thirdMatch.school_2_score) || 0;

      if (thirdSchool1Score > thirdSchool2Score) {
        club_3rd = getClubInfo(thirdMatch.school_1, "Third place", false);
        club_4th = getClubInfo(thirdMatch.school_2, "Fourth place", false);
      } else if (thirdSchool2Score > thirdSchool1Score) {
        club_3rd = getClubInfo(thirdMatch.school_2, "Third place", false);
        club_4th = getClubInfo(thirdMatch.school_1, "Fourth place", false);
      } else {
        // Handle tie with penalties
        const thirdSchool1PenaltyScore = Number(thirdMatch.school_1_penalty_score) || 0;
        const thirdSchool2PenaltyScore = Number(thirdMatch.school_2_penalty_score) || 0;

        if (thirdSchool1PenaltyScore > thirdSchool2PenaltyScore) {
          club_3rd = getClubInfo(thirdMatch.school_1, "Third place", false);
          club_4th = getClubInfo(thirdMatch.school_2, "Fourth place", false);
        } else if (thirdSchool2PenaltyScore > thirdSchool1PenaltyScore) {
          club_3rd = getClubInfo(thirdMatch.school_2, "Third place", false);
          club_4th = getClubInfo(thirdMatch.school_1, "Fourth place", false);
        } else {
          // Still tied or no penalty data
          club_3rd = getClubInfo(thirdMatch.school_1, "Third place", false);
          club_4th = getClubInfo(thirdMatch.school_2, "Fourth place", false);
        }
      }

      club_3rd.third_place_name = club_3rd.name;
      club_4th.fourth_place_name = club_4th.name;
    } else {
      // If no semifinal data either, use placeholders
      club_3rd = getClubInfo(null, "Third place", false);
      club_4th = getClubInfo(null, "Fourth place", false);

      club_3rd.third_place_name = "TBD";
      club_4th.fourth_place_name = "TBD";
    }
  } else {
    if (isThirdPlaceFinished) {
      // Third place match exists and has finished
      const thirdSchool1Score = Number(thirdMatch.school_1_score) || 0;
      const thirdSchool2Score = Number(thirdMatch.school_2_score) || 0;

      if (thirdSchool1Score > thirdSchool2Score) {
        club_3rd = getClubInfo(thirdMatch.school_1, "Third place", false);
        club_4th = getClubInfo(thirdMatch.school_2, "Fourth place", false);
      } else if (thirdSchool2Score > thirdSchool1Score) {
        club_3rd = getClubInfo(thirdMatch.school_2, "Third place", false);
        club_4th = getClubInfo(thirdMatch.school_1, "Fourth place", false);
      } else {
        // Handle tie with penalties
        const thirdSchool1PenaltyScore = Number(thirdMatch.school_1_penalty_score) || 0;
        const thirdSchool2PenaltyScore = Number(thirdMatch.school_2_penalty_score) || 0;

        if (thirdSchool1PenaltyScore > thirdSchool2PenaltyScore) {
          club_3rd = getClubInfo(thirdMatch.school_1, "Third place", false);
          club_4th = getClubInfo(thirdMatch.school_2, "Fourth place", false);
        } else if (thirdSchool2PenaltyScore > thirdSchool1PenaltyScore) {
          club_3rd = getClubInfo(thirdMatch.school_2, "Third place", false);
          club_4th = getClubInfo(thirdMatch.school_1, "Fourth place", false);
        } else {
          // Still tied or no penalty data
          club_3rd = getClubInfo(thirdMatch.school_1, "Third place", false);
          club_4th = getClubInfo(thirdMatch.school_2, "Fourth place", false);
        }
      }

      club_3rd.third_place_name = club_3rd.name;
      club_4th.fourth_place_name = club_4th.name;
    } else if (!knockout.third_place?.length && semifinalMatches.length >= 2) {
      // Cuma turnamen tanpa perebutan juara 3 yang ambil dari semifinal loser.
      // Kalau match-nya ada tapi belum kelar, juara 3 tetap TBD (jatuh ke else).

      // Process first semifinal match
      const semi1 = semifinalMatches[0] || {};
      const semi1School1Score = Number(semi1.school_1_score) || 0;
      const semi1School2Score = Number(semi1.school_2_score) || 0;

      // Process second semifinal match
      const semi2 = semifinalMatches[1] || {};
      const semi2School1Score = Number(semi2.school_1_score) || 0;
      const semi2School2Score = Number(semi2.school_2_score) || 0;

      // Determine losers from semifinal
      let semi1Loser = null;
      let semi2Loser = null;

      // First semifinal loser
      if (semi1School1Score !== undefined && semi1School2Score !== undefined) {
        if (semi1School1Score < semi1School2Score) {
          semi1Loser = semi1.school_1;
        } else if (semi1School2Score < semi1School1Score) {
          semi1Loser = semi1.school_2;
        } else {
          // Handle tie with penalties
          const semi1School1PenaltyScore = Number(semi1.school_1_penalty_score) || 0;
          const semi1School2PenaltyScore = Number(semi1.school_2_penalty_score) || 0;

          if (semi1School1PenaltyScore < semi1School2PenaltyScore) {
            semi1Loser = semi1.school_1;
          } else if (semi1School2PenaltyScore < semi1School1PenaltyScore) {
            semi1Loser = semi1.school_2;
          }
        }
      }

      // Second semifinal loser
      if (semi2School1Score !== undefined && semi2School2Score !== undefined) {
        if (semi2School1Score < semi2School2Score) {
          semi2Loser = semi2.school_1;
        } else if (semi2School2Score < semi2School1Score) {
          semi2Loser = semi2.school_2;
        } else {
          // Handle tie with penalties
          const semi2School1PenaltyScore = Number(semi2.school_1_penalty_score) || 0;
          const semi2School2PenaltyScore = Number(semi2.school_2_penalty_score) || 0;

          if (semi2School1PenaltyScore < semi2School2PenaltyScore) {
            semi2Loser = semi2.school_1;
          } else if (semi2School2PenaltyScore < semi2School1PenaltyScore) {
            semi2Loser = semi2.school_2;
          }
        }
      }

      // Order semifinal losers by goal difference or alphabetically if needed
      // For simplicity, just assign them in order here
      club_3rd = getClubInfo(semi1Loser, "Third place", false);
      club_4th = getClubInfo(semi2Loser, "Fourth place", false);

      club_3rd.third_place_name = club_3rd.name || "TBD";
      club_4th.fourth_place_name = club_4th.name || "TBD";
    } else {
      // If no semifinal data either, use placeholders
      club_3rd = getClubInfo(null, "Third place", false);
      club_4th = getClubInfo(null, "Fourth place", false);

      club_3rd.third_place_name = "TBD";
      club_4th.fourth_place_name = "TBD";
    }
  }

  rounds.push([
    {
      round_name: "Champion",
      club_1st,
      club_2nd,
      club_3rd,
      club_4th,
      champion_name: club_1st.name || "TBD",
      runner_up_name: club_2nd.name || "TBD",
      third_place_name: club_3rd.name || "TBD",
      fourth_place_name: club_4th.name || "TBD",
      is_final_finished: isFinalFinished,
      is_third_place_finished: isThirdPlaceFinished,
    },
  ]);

  return {
    name: "Fase Knockout",
    stage_type_id: 3,
    rounds,
    category,
  };
}
